import React from "react";
import TextInput from "../../UI/TextInput";
import { companyPluginUseCase } from "../../../../di/container";
import type { CompanyPluginDialogProps } from "./types";
import { PluginDialogFrame } from "./PluginDialogFrame";
import { resolveErrorMessage } from "../../../pages/CompanyAdmin/MallDesign/utils";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 홈택스 세금계산서 / 현금영수증 발급 플러그인 등록/수정.
 *
 * 예전에는 "홈택스 API 키" 를 적는 칸이었지만, 홈택스는 제3자 발급 API 를 열지 않는다.
 * 실제 발급은 국세청 인증 연동사를 거치므로 여기서는 세금계산서 공급자 담당자와 발급 방식만 받는다.
 * 저장하면 서버가 회사 사업자번호로 연동 회원을 확보한다(내 회사 정보의 사업자번호·상호·대표자·주소·전화가 필요).
 */
export const HomeTaxPluginDialog = ({
  companyId,
  initialConfig,
  onClose,
  onSaved,
  onDeleted,
}: CompanyPluginDialogProps<"HOME_TAX">) => {
  const [contactName, setContactName] = React.useState(initialConfig?.contactName ?? "");
  const [contactEmail, setContactEmail] = React.useState(initialConfig?.contactEmail ?? "");
  const [autoIssue, setAutoIssue] = React.useState(initialConfig?.autoIssue ?? false);
  // 예전 설정에는 값이 없다. 없으면 켜진 것으로 본다(서버 기본값과 같다)
  const [autoCancelOnRefund, setAutoCancelOnRefund] = React.useState(initialConfig?.autoCancelOnRefund ?? true);
  const [isBusy, setIsBusy] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const needsReconnect = initialConfig !== null && !initialConfig.memberId;

  const handleSubmit = async () => {
    if (!contactName.trim()) {
      setErrorMessage("세금계산서 담당자명을 입력해 주세요.");
      return;
    }
    if (!EMAIL_PATTERN.test(contactEmail.trim())) {
      setErrorMessage("세금계산서 담당자 이메일을 정확히 입력해 주세요.");
      return;
    }

    setIsBusy(true);
    setErrorMessage(null);
    try {
      const saved = await companyPluginUseCase.savePlugin(companyId, "HOME_TAX", {
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        autoIssue,
        autoCancelOnRefund,
      });
      onSaved(saved);
    } catch (error) {
      setErrorMessage(resolveErrorMessage(error, "홈택스 연동 저장에 실패했습니다."));
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async () => {
    setIsBusy(true);
    setErrorMessage(null);
    try {
      await companyPluginUseCase.deletePlugin(companyId, "HOME_TAX");
      onDeleted("HOME_TAX");
    } catch (error) {
      setErrorMessage(resolveErrorMessage(error, "연동 해제에 실패했습니다."));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <PluginDialogFrame
      title="홈택스 세금계산서 / 현금영수증 발급"
      isEditing={initialConfig !== null}
      isBusy={isBusy}
      errorMessage={errorMessage}
      onClose={onClose}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
    >
      {needsReconnect && (
        <div className="mb-[1rem] rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-[0.875rem] py-[0.75rem] text-[0.8125rem] text-[#4285F4]">
          예전에 등록한 홈택스 API 키로는 발급할 수 없습니다. 담당자 정보를 입력해 다시 저장하면 발급 연동이 완료됩니다.
        </div>
      )}

      <div className="text-[#555] mb-[0.625rem]">세금계산서 담당자명</div>
      <TextInput
        innerClassName="bg-gray-50"
        value={contactName}
        onChange={(e) => {
          setContactName(e.target.value);
          setErrorMessage(null);
        }}
        placeholder="세금계산서에 공급자 담당자로 표시됩니다."
      />

      <div className="text-[#555] mt-[1.25rem] mb-[0.625rem]">세금계산서 담당자 이메일</div>
      <TextInput
        innerClassName="bg-gray-50"
        value={contactEmail}
        onChange={(e) => {
          setContactEmail(e.target.value);
          setErrorMessage(null);
        }}
        placeholder="발급 사본과 국세청 처리 결과가 이 주소로 옵니다."
      />

      <div className="text-[#555] mt-[1.25rem] mb-[0.625rem]">발급 방식</div>
      <div role="radiogroup" className="grid grid-cols-2 gap-[0.5rem]">
        {[
          { value: false, name: "수동 발급", desc: "세금계산서/현금영수증 관리에서 확인 후 발급" },
          { value: true, name: "자동 발급", desc: "입금이 확인되면 고객 신청 건을 바로 발급" },
        ].map((option) => {
          const selected = autoIssue === option.value;
          return (
            <button
              key={option.name}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setAutoIssue(option.value)}
              className={`text-left px-[0.875rem] py-[0.75rem] rounded-[8px] border transition-all ${
                selected ? "border-[#4285F4] ring-2 ring-[#4285F4]/20" : "border-[#e0e0e0] hover:bg-[#fafafa]"
              }`}
            >
              <div className="font-medium text-[#222]">{option.name}</div>
              <div className="text-[0.75rem] text-[#888] mt-[0.25rem]">{option.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="text-[#555] mt-[1.25rem] mb-[0.625rem]">환불 시 현금영수증 자동 취소</div>
      <div role="radiogroup" className="grid grid-cols-2 gap-[0.5rem]">
        {[
          { value: true, name: "자동 취소", desc: "환불 처리하면 발급된 현금영수증을 바로 취소" },
          { value: false, name: "직접 처리", desc: "취소 건을 만들어 두고 담당자가 확인 후 실행" },
        ].map((option) => {
          const selected = autoCancelOnRefund === option.value;
          return (
            <button
              key={option.name}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setAutoCancelOnRefund(option.value)}
              className={`text-left px-[0.875rem] py-[0.75rem] rounded-[8px] border transition-all ${
                selected ? "border-[#4285F4] ring-2 ring-[#4285F4]/20" : "border-[#e0e0e0] hover:bg-[#fafafa]"
              }`}
            >
              <div className="font-medium text-[#222]">{option.name}</div>
              <div className="text-[0.75rem] text-[#888] mt-[0.25rem]">{option.desc}</div>
            </button>
          );
        })}
      </div>
      <p className="text-[0.75rem] text-[#757575] mt-2">
        주문을 환불 처리하면 발급된 현금영수증을 자동으로 취소합니다. 세금계산서는 국세청 전송 전이면 자동 취소, 전송
        후면 수정세금계산서 초안을 만들어 담당자가 확인 후 발급합니다.
      </p>

      <ul className="text-[0.75rem] text-[#757575] mt-3 list-disc pl-4 space-y-1">
        <li>내 회사 정보의 사업자등록번호·상호·대표자명·사업장 주소·대표 전화번호로 발급합니다.</li>
        <li>세금계산서는 회사 공동인증서(사업자 범용 또는 전자세금계산서용) 등록이 한 번 필요합니다. 세금계산서/현금영수증 관리 화면에서 등록할 수 있습니다.</li>
        <li>현금영수증은 인증서 없이 발급되며, 다음날 국세청으로 전송됩니다.</li>
      </ul>
    </PluginDialogFrame>
  );
};
